import { useState } from 'react';
import { toast } from '@/components/ui/sonner';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '@/components/ui/dialog';
import { useBusinessUnits } from '@/hooks/use-business-units';
import { useInboxMutations } from '@/hooks/use-inboxes';
import { PrimaryChangeConfirm } from './primary-change-confirm';
import {
  diffBusinessUnitSelection,
  firstTickedId,
  hasBusinessUnitSelectionChanged,
  inboxUnitIds,
  primaryFallbackOrder
} from '../inbox-units';
import type { BusinessUnit, Inbox } from '@/types';

interface Props {
  inbox: Inbox;
  trigger: React.ReactNode;
}

/**
 * Which business units an inbox belongs to, and which of them is primary.
 *
 * This only stages a selection — nothing is sent until Save, and Save sends the diff against
 * what the inbox had when the dialog opened, not a full replace. That keeps a no-op Save free
 * of side effects and means a slow network never risks reordering the operator's own edits.
 */
export function InboxSharingDialog({ inbox, trigger }: Props) {
  const { data: units = [] } = useBusinessUnits();
  const { shareWithBusinessUnit, unshareFromBusinessUnit, setPrimaryBusinessUnit } =
    useInboxMutations();
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [tickedIds, setTickedIds] = useState<number[]>([]);
  const [primaryId, setPrimaryId] = useState<number | null>(null);
  const [initialUnitIds, setInitialUnitIds] = useState<number[]>([]);
  const [initialPrimary, setInitialPrimary] = useState<number | null>(null);
  const [confirmingPrimary, setConfirmingPrimary] = useState(false);

  // A unit deactivated after it was assigned still owns the inbox — it is just not offered
  // as a NEW choice here, same as unassigned-inboxes-section treats an inactive unit.
  const activeUnits = units.filter((u) => u.status === 'active');

  // An inactive unit already on the inbox when the dialog opened still gets a row, so it can
  // be removed — just never one offered fresh, and never a candidate for "Make primary".
  const inactiveInboxUnits = units.filter(
    (u) => u.status !== 'active' && initialUnitIds.includes(u.id)
  );

  // Staged from the inbox at the moment the dialog OPENS, not whenever the inbox changes:
  // the list refetches on its own (every save, a window regaining focus), and re-staging
  // then would wipe the ticks the operator has made but not saved.
  const onOpenChange = (next: boolean) => {
    if (next) {
      const ids = inboxUnitIds(inbox);
      setTickedIds(ids);
      setPrimaryId(inbox.bu_id);
      setInitialUnitIds(ids);
      setInitialPrimary(inbox.bu_id);
    }
    setOpen(next);
  };

  const diff = diffBusinessUnitSelection(
    initialUnitIds,
    initialPrimary,
    tickedIds,
    primaryId
  );
  const hasChanges = hasBusinessUnitSelectionChanged(diff);
  // An inbox getting its FIRST primary is an assignment, not a change: nothing to confirm.
  const primaryIsChanging = diff.newPrimary != null && initialPrimary != null;
  const unitName = (buId: number | null) => units.find((u) => u.id === buId)?.name ?? null;

  // Ticking the first unit with no primary yet, or unticking the primary itself, both need a
  // new primary decided on the spot — the invariant is that a ticked unit always has one.
  const toggleUnit = (buId: number, ticked: boolean) => {
    const nextTicked = ticked
      ? [...tickedIds.filter((id) => id !== buId), buId]
      : tickedIds.filter((id) => id !== buId);

    let nextPrimary = primaryId;
    if (ticked && primaryId == null) {
      nextPrimary = buId;
    } else if (!ticked && primaryId === buId) {
      nextPrimary = firstTickedId(
        primaryFallbackOrder(
          initialUnitIds,
          tickedIds,
          activeUnits.map((u) => u.id)
        ),
        nextTicked
      );
    }

    setTickedIds(nextTicked);
    setPrimaryId(nextPrimary);
  };

  const onSave = async () => {
    setSaving(true);
    try {
      // Order matters: the new primary must land before its predecessor is removed, or the
      // removal would trigger machine's own auto-promotion instead and could pick differently.
      for (const buId of diff.toAdd) {
        await shareWithBusinessUnit(inbox.id, buId);
      }
      if (diff.newPrimary != null) {
        await setPrimaryBusinessUnit(inbox.id, diff.newPrimary);
      }
      for (const buId of diff.toRemove) {
        await unshareFromBusinessUnit(inbox.id, buId);
      }
      toast.success(`Business units updated for ${inbox.name}`);
      setOpen(false);
    } catch (err) {
      toast.error(
        err instanceof Error ? err.message : 'Could not update business units'
      );
    } finally {
      setSaving(false);
    }
  };

  // A changed primary reroutes every ticket whose customer is not found, so it asks first.
  const onSaveClick = () => {
    if (primaryIsChanging) setConfirmingPrimary(true);
    else void onSave();
  };

  // One row for a unit: a checkbox, its label, and — unless this unit can never be primary —
  // either the primary indicator or the button to make it so. Shared by the active list and
  // the inactive-but-still-on-the-inbox list below, which differ only in `canPromote`.
  const renderUnitRow = (unit: BusinessUnit, canPromote: boolean) => {
    const ticked = tickedIds.includes(unit.id);
    const primary = primaryId === unit.id;
    const checkboxId = `share-bu-${inbox.id}-${unit.id}`;
    return (
      <div
        key={unit.id}
        className='flex items-center justify-between gap-3 rounded-md border px-3 py-2'
      >
        <div className='flex items-center gap-2'>
          <Checkbox
            id={checkboxId}
            checked={ticked}
            disabled={saving}
            onCheckedChange={(checked) => toggleUnit(unit.id, checked === true)}
          />
          <Label htmlFor={checkboxId} className='text-sm font-normal text-foreground'>
            {unit.name}
          </Label>
          {!canPromote && <span className='text-xs text-muted-foreground'>(inactive)</span>}
        </div>
        {ticked &&
          (primary ? (
            <span className='text-xs text-muted-foreground'>Primary</span>
          ) : (
            canPromote && (
              <Button
                type='button'
                variant='ghost'
                size='sm'
                disabled={saving}
                onClick={() => setPrimaryId(unit.id)}
              >
                Make primary
              </Button>
            )
          ))}
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className='sm:max-w-md'>
        <DialogHeader>
          <DialogTitle>Business units for {inbox.name}</DialogTitle>
          <DialogDescription>
            Tickets arriving here are matched to a unit by the customer&apos;s Shopify store.
            When the customer is not found, the ticket goes to the primary unit.
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-2'>
          {inactiveInboxUnits.map((unit) => renderUnitRow(unit, false))}
          {activeUnits.map((unit) => renderUnitRow(unit, true))}
        </div>

        <DialogFooter>
          <DialogClose asChild>
            <Button type='button' variant='outline' disabled={saving}>
              Cancel
            </Button>
          </DialogClose>
          <Button type='button' onClick={onSaveClick} disabled={saving || !hasChanges}>
            {saving ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>

        <PrimaryChangeConfirm
          open={confirmingPrimary}
          onOpenChange={setConfirmingPrimary}
          inboxName={inbox.name}
          toUnitName={unitName(diff.newPrimary) ?? 'the new primary'}
          fromUnitName={unitName(initialPrimary)}
          onConfirm={onSave}
        />
      </DialogContent>
    </Dialog>
  );
}
